import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import Media from 'react-media'
import Carousel from 'nuka-carousel'
import PlanItem from 'components/PlanItem'
import PlanSummary from 'components/PlanItem/PlanSummary'
import styles from './styles.scss'

// 3 plans with a set width of 380 + padding on desktop = 1180 so we change the layout slightly
// below this width so users can still see all the plans clearly on medium sized devices
const desktopBreakpoint = 1180

class PlanDisplay extends PureComponent {
  constructor(props) {
    super(props)
    const { plans } = props
    const defaultPlanIndex = plans.map((p, i) => (p.carouselDefault ? i : null)).filter(i => i != null)[0] || 0
    this.state = {
      slideIndex: defaultPlanIndex,
      isDesktop: window.innerWidth >= desktopBreakpoint,
    }
  }

  componentDidMount() {
    window.addEventListener('resize', this.updateDimensions)
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateDimensions)
  }

  getPlans = () => {
    const {
      plans,
      summary,
      buttonTexts,
      onButtonClicks,
      showEligibility,
    } = this.props
    const { isDesktop, slideIndex } = this.state
    const PlanComponent = summary ? PlanSummary : PlanItem
    let activatePlans = []

    if (isDesktop) {
      activatePlans = [slideIndex - 1, slideIndex, slideIndex + 1]
    }
    return plans.map((planData, i) => (
      <PlanComponent
        key={planData.id}
        {...planData}
        active={isDesktop ? activatePlans.includes(i) : i === slideIndex}
        buttonText={buttonTexts[i]}
        onButtonClick={onButtonClicks[i]}
        showEligibility={showEligibility}
      />
    ))
  }

  updateDimensions = () => {
    this.setState({ isDesktop: window.innerWidth >= desktopBreakpoint })
  }

  changeSlideIndex = slideIndex => this.setState({ slideIndex })

  // Displays the left slider control button if it's rendered on a desktop screen width, the number of plans
  // are more than 3 and if the centered plan is not the far left slide. On mobile it displays the left control
  // always unless the center slide is the far left slide.
  showLeftControl = () => {
    const { plans } = this.props
    const { isDesktop, slideIndex } = this.state

    if (isDesktop) {
      return plans.length > 3 ? (
        slideIndex > 1
      ) : false
    }
    return slideIndex > 0
  }

  // Displays the right slider control button if it's rendered on a desktop screen width, the number of plans
  // are more than 3 and if the centered plan is not the far right slide. On mobile it displays the right control
  // always unless the center slide is the far right slide.
  showRightControl = () => {
    const { plans } = this.props
    const { isDesktop, slideIndex } = this.state

    if (isDesktop) {
      return plans.length > 3 ? (
        slideIndex < plans.length - 2
      ) : false
    }
    return slideIndex < plans.length - 1
  }

  leftControls = previousSlide => (
    this.showLeftControl() ? (
      <button className={styles.carouselButton} onClick={previousSlide}>
        <i className="kgn-icon-arrow-left2" style={{ lineHeight: 'normal', color: '#fff' }} />
      </button>
    ) : () => null
  )

  rightControls = nextSlide => (
    this.showRightControl() ? (
      <button className={styles.carouselButton} onClick={nextSlide}>
        <i className="kgn-icon-arrow-right2" style={{ lineHeight: 'normal', color: '#fff' }} />
      </button>
    ) : () => null
  )

  // Desktop slider displays the center 3 slides with opacity 1 and any slides left and right of
  // the center 3 slides with opacity 0.4. Each slide has a set width of 380px.
  renderPlanItemsDesktop = () => {
    const { plans } = this.props
    const { isDesktop, slideIndex } = this.state

    return (
      <div className={styles.planList}>
        <Carousel
          slideIndex={slideIndex}
          afterSlide={this.changeSlideIndex}
          speed={100}
          renderBottomCenterControls={null}
          cellAlign="center"
          cellSpacing={15}
          heightMode="max"
          slidesToShow={isDesktop ? plans.length - 1 : 1.2}
          frameOverflow="visible"
          slideWidth="380px"
          width="100%"
          dragging={false}
          renderCenterLeftControls={({ previousSlide }) => this.leftControls(previousSlide)}
          renderCenterRightControls={({ nextSlide }) => this.rightControls(nextSlide)}
        >
          {this.getPlans()}
        </Carousel>
      </div>
    )
  }

  // Mobile slider displays the center slide with opacity 1 and any slides left and right of
  // the center slide with opacity 0.4.
  renderPlanItemsMobile = () => {
    const { slideIndex } = this.state

    return (
      <div className={styles.planList}>
        <Carousel
          slideIndex={slideIndex}
          afterSlide={this.changeSlideIndex}
          speed={100}
          renderBottomCenterControls={null}
          cellAlign="center"
          heightMode="max"
          slidesToShow={1.2}
          renderCenterLeftControls={({ previousSlide }) => this.leftControls(previousSlide)}
          renderCenterRightControls={({ nextSlide }) => this.rightControls(nextSlide)}
        >
          {this.getPlans()}
        </Carousel>
      </div>
    )
  }

  render() {
    const { summary, plans } = this.props
    return (
      <div className={styles.planWrapper}>
        {plans.length < 2
          ? <ul className={styles.planList}>{this.getPlans()}</ul>
          : (
            <Media query="(max-width: 480px)">
              {matches => (matches && !summary ? this.renderPlanItemsMobile() : this.renderPlanItemsDesktop())}
            </Media>
          )
        }
      </div>
    )
  }
}

PlanDisplay.propTypes = {
  plans: PropTypes.arrayOf(PropTypes.shape({
    planColor: PropTypes.string,
    secondaryPlanColor: PropTypes.string,
    isPromo: PropTypes.bool,
    promoText: PropTypes.string,
    rate: PropTypes.number,
    rateRrp: PropTypes.string,
    title: PropTypes.string,
    headerPill: PropTypes.string,
    interval: PropTypes.string,
    limit: PropTypes.string,
    speedDisclaimer: PropTypes.string,
    features: PropTypes.string,
    buttonText: PropTypes.string,
    terms: PropTypes.string,
    pricingInfo: PropTypes.string,
    onButtonClick: PropTypes.func,
  })),
  buttonTexts: PropTypes.arrayOf(PropTypes.string),
  onButtonClicks: PropTypes.arrayOf(PropTypes.func),
  summary: PropTypes.bool,
  showEligibility: PropTypes.bool,
}

PlanDisplay.defaultProps = {
  plans: [],
  buttonTexts: [''],
  summary: false,
  onButtonClicks: [f => f],
  showEligibility: true,
}

export default PlanDisplay



// WEBPACK FOOTER //
// ./components/PlanDisplay/index.jsx